import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Navbar from '../component/Navbar/Main'
import Footer from '../component/Footer/Main'
import ProtectedRoute from '../component/Manage/ProtectedRoute'
import RoleGuard from '../component/Manage/RoleGuard'

// Public pages
const Home = lazy(() => import('../component/Home/Main'));
const About = lazy(() => import('../component/About/Main'));
const OurRoom = lazy(() => import('../component/Rooms/OurRoom/Main'));
const RoomDetails = lazy(() => import('../component/Rooms/RoomDetails/Main'));
const Service = lazy(() => import('../component/Facilities/Service/Main'));
const ServiceDetails = lazy(() => import('../component/Facilities/ServiceDetails/Main'));
const Faq = lazy(() => import('../component/Faq/Main'));
const Contact = lazy(() => import('../component/Contact/Main'));

// Manage pages
const ManageLogin = lazy(() => import('../component/Manage/Login'));
const ManageLayout = lazy(() => import('../component/Manage/Layout'));
const ManageDashboard = lazy(() => import('../component/Manage/Dashboard'));
const ManageUsers = lazy(() => import('../component/Manage/pages/Users'));
const ManageCategories = lazy(() => import('../component/Manage/pages/Categories'));
const ManageItems = lazy(() => import('../component/Manage/pages/Items'));
const ManageTransactions = lazy(() => import('../component/Manage/pages/Transactions'));
const ManageRooms = lazy(() => import('../component/Manage/pages/Rooms'));
const ManageRoomCleaning = lazy(() => import('../component/Manage/pages/RoomCleaning'));

const Routing = () => {
    const [showNav, setShowNav] = useState(true);
    const [showFooter, setShowFooter] = useState(true);
    const location = useLocation();

    const isManagePage = location.pathname.startsWith('/manage');

    useEffect(() => {
        if (isManagePage) {
            setShowNav(false);
            setShowFooter(false);
            return;
        }
        const isHome3 = location.pathname === "/home-03";
        const isHome4 = location.pathname === "/home-04";
        setShowNav(!isHome3 && !isHome4);
        setShowFooter(!isHome3);
        window.scrollTo(0, 0);
    }, [location.pathname, isManagePage]);

    return (
        <>
            {showNav && <Navbar />}
            <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
                <Routes>
                    {/* ── Public routes ── */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/room" element={<OurRoom />} />
                    <Route path="/room-details" element={<RoomDetails />} />
                    <Route path="/amenities" element={<Service />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/service-details" element={<ServiceDetails />} />

                    {/* ── Manage routes ── */}
                    <Route path="/manage/login" element={<ManageLogin />} />
                    <Route
                        path="/manage"
                        element={
                            <ProtectedRoute>
                                <ManageLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/manage/dashboard" replace />} />
                        <Route path="dashboard" element={<ManageDashboard />} />
                        <Route path="users" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor', 'housekeeper_leader']}>
                                <ManageUsers />
                            </RoleGuard>
                        } />
                        <Route path="rooms" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}>
                                <ManageRooms />
                            </RoleGuard>
                        } />
                        <Route path="supply/categories" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor', 'housekeeper_leader']}>
                                <ManageCategories />
                            </RoleGuard>
                        } />
                        <Route path="supply/items" element={<ManageItems />} />
                        <Route path="supply/transactions" element={<ManageTransactions />} />
                        <Route path="room-cleaning" element={<ManageRoomCleaning />} />
                    </Route>
                </Routes>
            </Suspense>
            {showFooter && <Footer />}
        </>
    );
};

export default Routing;
